import { useEffect, useReducer, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import { COMMAND_PRIORITY_LOW } from 'lexical';
import {
  ELEMENT_SELECTED_COMMAND,
  INPUT_FOCUSED_COMMAND,
  EDIT_MODE_EXITED_COMMAND,
  INSERT_TENTATIVE_ITEM_COMMAND,
  REMOVE_ALL_TENTATIVE_COMMAND,
  CONVERT_TENTATIVE_TO_PERMANENT_COMMAND,
  ElementSelectedPayload,
} from './PromptCommands';

// State Machine States
type State =
  | { type: 'IDLE' }
  | { type: 'TENTATIVE'; elementId: string; label: string };

// State Machine Events
type Event =
  | { type: 'ELEMENT_SELECTED'; payload: ElementSelectedPayload }
  | { type: 'INPUT_FOCUSED' }
  | { type: 'EDIT_MODE_EXITED' };

// State Machine Reducer
function tentativeItemReducer(state: State, event: Event): State {
  switch (state.type) {
    case 'IDLE':
      if (event.type === 'ELEMENT_SELECTED') {
        return {
          type: 'TENTATIVE',
          elementId: event.payload.editId,
          label: event.payload.label,
        };
      }
      return state;

    case 'TENTATIVE':
      if (event.type === 'ELEMENT_SELECTED') {
        // Replace with new tentative item
        return {
          type: 'TENTATIVE',
          elementId: event.payload.editId,
          label: event.payload.label,
        };
      }
      if (event.type === 'INPUT_FOCUSED') {
        // Convert to permanent and go back to IDLE
        // The permanent item now exists in the editor but we're ready for new tentative items
        return { type: 'IDLE' };
      }
      if (event.type === 'EDIT_MODE_EXITED') {
        // Remove tentative item
        return { type: 'IDLE' };
      }
      return state;


    default:
      return state;
  }
}

export default function TentativeItemPlugin() {
  const [editor] = useLexicalComposerContext();
  const [state, dispatch] = useReducer(tentativeItemReducer, { type: 'IDLE' });

  // Register command listeners
  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        ELEMENT_SELECTED_COMMAND,
        (payload: ElementSelectedPayload) => {
          lastEventRef.current = 'ELEMENT_SELECTED';
          dispatch({ type: 'ELEMENT_SELECTED', payload });
          return false; // Allow other handlers to run
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        INPUT_FOCUSED_COMMAND,
        () => {
          lastEventRef.current = 'INPUT_FOCUSED';
          dispatch({ type: 'INPUT_FOCUSED' });
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        EDIT_MODE_EXITED_COMMAND,
        () => {
          lastEventRef.current = 'EDIT_MODE_EXITED';
          dispatch({ type: 'EDIT_MODE_EXITED' });
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor]);

  // Track the last event to determine transition cause
  const lastEventRef = useRef<Event['type'] | null>(null);

  // Execute side effects based on state transitions
  useEffect(() => {
    // Defer command dispatching to avoid flushSync warning
    // Use queueMicrotask to schedule after current render cycle
    queueMicrotask(() => {
      if (state.type === 'TENTATIVE') {
        // Insert or replace tentative item
        editor.dispatchCommand(REMOVE_ALL_TENTATIVE_COMMAND, undefined);
        editor.dispatchCommand(INSERT_TENTATIVE_ITEM_COMMAND, {
          label: state.label,
          editId: state.elementId,
        });
      } else if (state.type === 'IDLE') {
        // Check if we came from focus event (convert to permanent) or other (just remove)
        if (lastEventRef.current === 'INPUT_FOCUSED') {
          editor.dispatchCommand(CONVERT_TENTATIVE_TO_PERMANENT_COMMAND, undefined);
        } else {
          editor.dispatchCommand(REMOVE_ALL_TENTATIVE_COMMAND, undefined);
        }
      }
    });
  }, [state, editor]);

  return null;
}
